/**
 * SearchPage — live search over the chosen college's catalogue.
 * Reads ?q= from the URL (so results are shareable/bookmarkable) and queries
 * the backend, which matches on name, brand and description.
 */

import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Container } from '@/components/layout/Container';
import { ProductCard } from '@/components/product/ProductCard';
import { ProductCardSkeleton, EmptyState, ErrorState } from '@/components/ui';
import { useCollege } from '@/context/CollegeContext';
import { useProducts } from '@/hooks/useStorefront';

export function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q')?.trim() ?? '';
  const { collegeId } = useCollege();

  const { data, isLoading, isError, error, refetch } = useProducts({
    collegeId,
    search: query || undefined,
    limit: 30,
  });
  const products = data?.items ?? [];

  return (
    <Container className="py-4">
      <h1 className="mb-4 text-xl font-bold text-ink">
        {query ? `Results for “${query}”` : 'Search'}
      </h1>

      {!query ? (
        <EmptyState
          icon={Search}
          title="Start typing to search"
          description="Search for any product in your campus store."
        />
      ) : isError ? (
        <ErrorState error={error} onRetry={refetch} />
      ) : isLoading ? (
        <div className="grid grid-cols-3 gap-x-2.5 gap-y-5 sm:grid-cols-4 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      ) : products.length === 0 ? (
        <EmptyState
          icon={Search}
          title={`No results for “${query}”`}
          description="Try a different name or brand."
        />
      ) : (
        <div className="grid grid-cols-3 gap-x-2.5 gap-y-5 sm:grid-cols-4 lg:grid-cols-6">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </Container>
  );
}

export default SearchPage;
